import { useMemo, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO, setCurrentStageName } from '../../reducers/rtfoSlice';

import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';

export const RTFOHistoricChart = ({ gridView, selectedStages, historicTraces, normalizeBy="proppant", chartType }) => {
  
  const theme = useTheme();
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const dispatch = useDispatch();

  const currentStageName = currentRTFO?.currentStageName;
  const currentStage = currentRTFO?.stageData?.[currentStageName];

  const darkMode = user?.value?.darkMode;

  const [xMax, setXMax] = useState(0);

  const data = useMemo(() => {
    let traces = [];

    // add all background traces
    selectedStages?.filter(name => name !== currentStageName)?.forEach(stageName => {
      traces = traces.concat(addTraces(stageName));
    });
    
    // add the current trace on top
    traces = traces.concat(addTraces(currentStageName));

    function addTraces(stageName) {
      try {
        const stage = currentRTFO?.stageData?.[stageName]; 
        const data = stage?.coldboreData;

        const wellName = stageName?.split('_')?.[1]?.toLowerCase();
        const trimmedStageNumber = String(Number(String(stageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
        const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters;
        const clusterNames = stageClusters && Object.keys(stageClusters);
        const numClusters = clusterNames?.length || 0;

        if (data && data.length > 0) {
         
          let X = [];
          let YPressure = [];
          let YRate = [];
          let YProppant = [];
          let YFR = [];
          let YFR2 = [];
          let YRNP = [];
          let YMFR = [];

          
          const startMinutes = data?.find(d => (normalizeBy === 'proppant' ? d.proppant : d.clean) > 0)?.time || data?.[0]?.time;

          data?.forEach((d,i) => {
            let x = 0;
            if (chartType === 'time') {
              x = d.time - startMinutes + 20;
            } else if (chartType === 'cumVolume') {
              x = d.cleanVolume;
            } else if (chartType === 'cumProppant') {
              const proppantMass = (((d?.clean || 0) * (i === 0 ? d?.time : (d?.time - data?.[i-1]?.time))) * (d?.proppant || 0) / 1000) || 0;
              x = i === 0 ? proppantMass : proppantMass + X[i-1];
            };

            X.push(x);
            YFR.push(d.fr || 0);
            YFR2.push(d.fr2 || 0);
            YProppant.push(d.proppant || 0);
            YRate.push(d.rate || 0);
            YPressure.push(d.pressure || 0);
            YRNP.push((d.pressure > 0 && d.rate > 1) ? (d.pressure / d.rate * 2 / 3) : null);
            YMFR.push((d.rate > 0 && d.proppantBH > 0 && numClusters > 0 ) ? (d.rate * d.proppantBH / 60 / numClusters) : null);
          });

          if (Math.max(...X) > xMax) {
            setXMax(Math.max(...X) * 1.1);
          };

          const proppantTrace = { // proppant
            x: X,
            y: YProppant,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y3',
            name: stage?.name?.split("_")?.slice(1)?.join("_")+'-Prop. Surf. (kg/m3)',
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: chartColors[4],
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5)
          };
          const frTrace = { // dry fr
            x: X,
            y: YFR,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y2',
            name: stage?.name?.split("_")?.slice(1)?.join("_")+'-DFR (kg/m3)',
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: chartColors[8],
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5)
          };
          const fr2Trace = { // liquid fr
            x: X,
            y: YFR2,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y2',
            name: stage?.name?.split("_")?.slice(1)?.join("_")+'-LFR (L/m3)',
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: chartColors[8],
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dot')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5)
          };
          const rateTrace = { // rate
            x: X,
            y: YRate,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y2',
            name: stage.name?.split("_")?.slice(1)?.join("_")+'-Rate (m3/min)',
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: chartColors[3],
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5),
            connectgaps: false
          };
          const rnpTrace = { // rnp
            x: X,
            y: YRNP,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y4',
            name: stage.name?.split("_")?.slice(1)?.join("_")+'-Fracfeed',
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: chartColors[1],
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5),
            connectgaps: false
          };
          let massFlowRateTrace = {
            x: X,
            y: YMFR,
            type: 'scattergl',
            mode: 'line',
            yaxis: 'y2',
            xaxis: 'x1',
            name: stage?.name?.split("_")?.slice(1)?.join("_")+'-MFR/Cluster (kg/s)',
            line: {
              width: 2,
              color: 'red',
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5),
            connectgaps: true
          };
          const pressureTrace = { // pressure
            x: X,
            y: YPressure,
            mode: 'line',
            type: 'scattergl',
            xaxis: 'x1',
            yaxis: 'y1',
            name: stage.name?.split("_")?.slice(1)?.join("_")+'-Pressure (MPa)',
            line: {
              width: (stage?.name === currentRTFO?.currentStageName ? 2 : 2),
              color: chartColors[2],
              dash: (stage?.name === currentRTFO?.currentStageName ? 'solid' : 'dash')
            },
            opacity: (stage?.name === currentRTFO?.currentStageName ? 1 : 0.5),
            connectgaps: false
          };

          const stageTraces = [];

          if (historicTraces?.indexOf('fr') !== -1) {
            stageTraces.push(frTrace);
            stageTraces.push(fr2Trace);
          } else {
            stageTraces.push({...frTrace, x: [], y: []});
            stageTraces.push({...fr2Trace, x: [], y: []});
          };
          if (historicTraces?.indexOf('proppant') !== -1) {
            stageTraces.push(proppantTrace);
          } else {
            stageTraces.push({...proppantTrace, x: [], y: []});
          };
          if (historicTraces?.indexOf('fracfeed') !== -1) {
            stageTraces.push(rnpTrace);
          } else {
            stageTraces.push({...rnpTrace, x: [], y: []});
          };
          if (historicTraces?.indexOf('mfr') !== -1) {
            stageTraces.push(massFlowRateTrace);
          } else {
            stageTraces.push({...massFlowRateTrace, x: [], y: []});
          };
          if (historicTraces?.indexOf('rate') !== -1) {
            stageTraces.push(rateTrace);
          } else {
            stageTraces.push({...rateTrace, x: [], y: []});
          };
          if (historicTraces?.indexOf('pressure') !== -1) {
            stageTraces.push(pressureTrace);
          } else {
            stageTraces.push({...pressureTrace, x: [], y: []});
          };

          return stageTraces;
        } else {
          return [];
        };
      } catch (error) {
        console.log(error);
        return [];
      };
    };

    return traces;

  }, [selectedStages, currentStageName, currentRTFO?.stageData, currentRTFO?.perfSchedule?.wells, currentRTFO?.currentStageName, xMax, historicTraces, normalizeBy, chartType]);

  const layout = useMemo(() => {
    return {
     // grid: {rows: 3, columns: 1, pattern: 'independent'},
      yaxis: { // Treating pressure y axis
        linecolor: chartColors[2],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[2],
        title: {
          text: 'Treating Pressure (MPa)',
          font: {
            size: 12
          }
        },
        anchor: 'x',
        range: [0, 100],
        // domain: [0.7, 1],
        side: 'left'
      },
      yaxis2: { // Slurry rate y axis
        linecolor: chartColors[3],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[3],
        title: {
          text: 'Sl. Rate (m3/min), FR (kg/m3 or L/m3) and MFR/Cluster (kg/s)',
          font: {
            size: 12
          },
        },
        range: [0, 25],
        anchor: 'free',
        overlaying: 'y',
        side: 'left',
        position: 0
      },
      yaxis3: { // Prop conc surf y axis
        linecolor: chartColors[4],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[4],
        title: {
          text: 'Proppant Concentration (kg/m3)',
          font: {
            size: 12
          }
        },
        range: [0, 1000],
        overlaying: 'y',
        side: 'right',
        anchor: 'x',
      },
      yaxis4: { // RNP y axis
        linecolor: chartColors[1],
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: chartColors[1],
        title: {
          text: 'Fracfeed',
          font: {
            size: 12
          }
        },
        range: [0, 10],
        overlaying: 'y',
        side: 'right',
        anchor: 'free',
        position: 1
      },
      xaxis: { // x axis
        range: chartType === 'time' ? [-5, xMax] : chartType === 'cumVolume' ? [-20, xMax] : [-5, xMax],
        title: {
          text: chartType === 'time' ? 'Time (min)' : chartType === 'cumVolume' ? 'Cumulative Clean Volume (m3)' : chartType === 'cumProppant' ? 'Cumulative Proppant (ton)' : '',
          font: {
            size: 12
          }
        },
        domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.9 : 0.95)],
        linecolor: darkMode ? 'lightgrey' : 'black',
        gridcolor: darkMode ? 'grey' : 'lightgrey',
        color: darkMode ? 'lightgrey' : 'black',
        anchor: 'y',
        showspikes: false,
        spikethickness: 2,
        spikedash: "dot",
        spikecolor: "#999999",
        spikemode: "across"
      },
      autosize: true,
      showlegend: false,
      legend: {
        orientation: "h",
        yanchor: "top",
        font: {
          color: darkMode ? 'lightgrey' : 'grey'
        },
        y: -0.2,
      },
      annotations: [
        {
          text: "Pumping History",
            font: {
            size: 14,
              color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: (gridView ? 1.125 : 1.075), //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
        {
          text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
            font: {
            size: 10,
              color: darkMode ? 'lightgrey' : 'black'
          },
          showarrow: false,
          align: 'center',
          x: 0.5, //position in x domain
          y: (gridView ? 1.075 : 1.035), //position in y domain
          xref: 'paper',
          yref: 'paper',
        },
      ],
      uirevision: true,
      margin: {"t": 60, "l": 60, "r": 60, "b": 60},
      images: [
        {
          x: (gridView ? 0.05 : 0.035),
          y: (gridView ? 1.05 : 1.025),
          sizex: (gridView ? 0.1 : 0.05),
          sizey: 0.1,
          source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
          xref: "page",
          yref: "y1 domain",
          xanchor: "right",
          yanchor: "bottom",
          opacity: 0.75
        }
      ],
      plot_bgcolor: "transparent",
      paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType, xMax]);

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 480,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
      onClick={handleChartClick}
    />
  );

  function handleChartClick(e) {
    try {
      const traceClicked = e.points?.[0]?.data?.name;
      const wellName = traceClicked?.split('_')?.[0];
      let stageNumber = traceClicked?.split('_')?.[1];
      stageNumber = stageNumber?.split('-')?.[0];
      const newStageName = `${currentRTFO?.name}_${wellName}_${stageNumber}`;

      if (newStageName && currentRTFO?.stageData?.[newStageName] && !currentRTFO?.isLoadingData) {
        dispatch(setCurrentStageName(newStageName));
      };
    } catch (error) {
      console.log(error);
    };
  };
};
